import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ArrowLeft, ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Shell";
import { AVATAR_SRC } from "@/components/clinic/SelvixAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { OperatorPath } from "@/components/panels/OperatorPath";
import { OperatorDetails } from "@/components/panels/OperatorDocs";
import { EmailConfirm } from "@/components/panels/EmailConfirm";

const GoogleG = () => (
  <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.4 30.2 0 24 0 14.6 0 6.6 5.4 2.7 13.3l7.8 6.1C12.4 13.6 17.7 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z" />
    <path fill="#FBBC05" d="M10.5 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.6l-7.8-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.9-6.1z" />
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.8 2.3-8.4 2.3-6.3 0-11.6-4.1-13.5-9.8l-7.9 6.1C6.6 42.6 14.6 48 24 48z" />
  </svg>
);

export function googleLogin(path = "/") {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + path;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export async function identityStart(provider, mode, linkRequestId) {
  try {
    const { data } = await api.post(`/identity/${provider}/start`, { mode, link_request_id: linkRequestId });
    window.location.href = data.redirect;
  } catch (e) {
    toast.error(errMsg(e));
  }
}

function ProviderButtons({ verb }) {
  return (
    <div className="space-y-2.5">
      <button data-testid={`${verb}-spid-btn`} onClick={() => identityStart("spid", "login")} className="flex w-full items-center justify-between rounded-xl bg-[#0066CC] px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-[#004c99]">
        <span className="flex items-center gap-3"><span className="rounded bg-white px-1.5 py-0.5 text-[11px] font-black text-[#0066CC]">SPID</span>{verb === "register" ? "REGISTRATI CON SPID" : "Entra con SPID"}</span>
        <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px]">SANDBOX</span>
      </button>
      <button data-testid={`${verb}-cie-btn`} onClick={() => identityStart("cie", "login")} className="flex w-full items-center justify-between rounded-xl bg-[#1A3E6F] px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-[#12304f]">
        <span className="flex items-center gap-3"><span className="rounded bg-white px-1.5 py-0.5 text-[11px] font-black text-[#1A3E6F]">CIE</span>{verb === "register" ? "REGISTRATI CON CIE" : "Entra con CIE"}</span>
        <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px]">SANDBOX</span>
      </button>
      <button data-testid={`${verb}-google-btn`} onClick={() => googleLogin("/")} className="flex w-full items-center gap-3 rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-50">
        <GoogleG /> CONTINUA CON GOOGLE
      </button>
    </div>
  );
}

function LoginForm({ onForgot }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "", mfa_code: "" });
  const [mfa, setMfa] = useState(false);
  const [enroll, setEnroll] = useState(null);
  const [busy, setBusy] = useState(false);
  const done = (data) => {
    setUser(data);
    navigate(params.get("next") || (data.role === "super_admin" ? "/superadmin" : "/"));
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { ...f, mfa_code: f.mfa_code || undefined });
      if (data.mfa_enroll_required) {
        const { data: s } = await api.post("/auth/mfa/enroll-start", { token: data.enroll_token });
        setEnroll({ token: data.enroll_token, ...s });
        return;
      }
      if (data.mfa_required) {
        setMfa(true);
        return;
      }
      done(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const finishEnroll = async () => {
    try {
      const { data } = await api.post("/auth/mfa/enroll-finish", { token: enroll.token, code: f.mfa_code });
      toast.success("Autenticazione a due fattori attivata");
      done(data);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  if (enroll) {
    return (
      <div className="space-y-3" data-testid="mfa-enroll">
        <p className="font-display text-base font-bold text-slate-900">Primo accesso: configura l'autenticazione a due fattori</p>
        <p className="text-xs text-slate-600">Il tuo ruolo richiede la MFA obbligatoria. Scansiona il QR con un'app di autenticazione (Google Authenticator, Microsoft Authenticator, Authy) e inserisci il codice a 6 cifre.</p>
        <img src={enroll.qr_svg} alt="QR MFA" className="mx-auto h-44 w-44 rounded-xl border border-slate-200 bg-white p-2" data-testid="mfa-enroll-qr" />
        <p className="break-all text-center font-mono text-[11px] text-slate-500" data-testid="mfa-enroll-secret">{enroll.secret}</p>
        <Input data-testid="mfa-enroll-code" inputMode="numeric" placeholder="Codice a 6 cifre" value={f.mfa_code} onChange={(e) => setF({ ...f, mfa_code: e.target.value })} />
        <Button data-testid="mfa-enroll-submit" onClick={finishEnroll} className="w-full rounded-full bg-sky-600 hover:bg-sky-700">Attiva e accedi</Button>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="login-form">
      <div className="space-y-1.5"><Label>Email</Label><Input data-testid="login-email-input" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
      <div className="space-y-1.5"><Label>Password</Label><Input data-testid="login-password-input" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
      {mfa && <div className="space-y-1.5"><Label>Codice MFA (app di autenticazione)</Label><Input data-testid="login-mfa-input" inputMode="numeric" value={f.mfa_code} onChange={(e) => setF({ ...f, mfa_code: e.target.value })} /></div>}
      <Button data-testid="login-submit-btn" disabled={busy} className="w-full rounded-full bg-sky-600 hover:bg-sky-700">Accedi</Button>
      <button type="button" data-testid="forgot-password-link" onClick={onForgot} className="text-xs font-semibold text-sky-700 hover:underline">Password dimenticata?</button>
    </form>
  );
}

function SandboxMailNotice({ link }) {
  if (!link) return null;
  return (
    <div data-testid="sandbox-email-notice" className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
      <b>EMAIL SIMULATA (nessun invio reale):</b> nessun provider email è configurato. Apri il link che riceveresti via email:{" "}
      <Link data-testid="sandbox-email-link" to={link} className="font-bold text-sky-700 underline">apri link</Link>
    </div>
  );
}

function ForgotForm({ onBack }) {
  const [email, setEmail] = useState("");
  const [link, setLink] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      toast.success(data.message);
      setLink(data.sandbox_email_link);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="forgot-form">
      <button type="button" onClick={onBack} className="flex items-center gap-1 text-xs font-semibold text-slate-500"><ArrowLeft className="h-3 w-3" /> Indietro</button>
      <Label>Email dell'account</Label>
      <Input data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      <Button data-testid="forgot-submit-btn" className="w-full rounded-full bg-sky-600 hover:bg-sky-700">Invia link di recupero</Button>
      <SandboxMailNotice link={link} />
    </form>
  );
}

function RegisterEmailForm({ onBack, usage, onRegistered, preEmail, precheckToken }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ first_name: "", last_name: "", email: preEmail || "", password: "", password_confirm: "", privacy_accepted: false });
  const [link, setLink] = useState(null);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/register", { ...f, usage_type: usage, precheck_token: precheckToken });
      setUser(data);
      setLink(data.sandbox_email_link);
      setDone(true);
      toast.success(precheckToken ? "Account creato" : "Account creato. Verifica la tua email.");
      onRegistered?.();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  if (done) {
    return (
      <div className="space-y-3" data-testid="register-success">
        <p className="text-sm text-slate-700">Ti abbiamo inviato un link di verifica (valido 24 ore) a <b>{f.email}</b>.</p>
        <SandboxMailNotice link={link} />
        {usage && usage !== "paziente" && <p className="text-xs text-slate-600" data-testid="operator-verification-notice">La categoria scelta <b>non attribuisce autorizzazioni</b>: per offrire o richiedere prestazioni completa la verifica dell'identità e la certificazione, revisionata da un amministratore.</p>}
        <Button data-testid="register-continue-onboarding" onClick={() => navigate(usage ? (usage === "paziente" ? "/" : "/profilo?tab=certificazione") : "/onboarding")} className="w-full rounded-full bg-sky-600 hover:bg-sky-700">Continua con la configurazione</Button>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="register-email-form">
      <button type="button" onClick={onBack} className="flex items-center gap-1 text-xs font-semibold text-slate-500"><ArrowLeft className="h-3 w-3" /> Altre modalità</button>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Nome</Label><Input data-testid="register-firstname-input" required value={f.first_name} onChange={set("first_name")} /></div>
        <div className="space-y-1.5"><Label>Cognome</Label><Input data-testid="register-lastname-input" required value={f.last_name} onChange={set("last_name")} /></div>
      </div>
      <div className="space-y-1.5"><Label>Email</Label><Input data-testid="register-email-input" type="email" required disabled={!!preEmail} value={f.email} onChange={set("email")} /></div>
      <div className="space-y-1.5"><Label>Password</Label><Input data-testid="register-password-input" type="password" required value={f.password} onChange={set("password")} /></div>
      <div className="space-y-1.5"><Label>Conferma password</Label><Input data-testid="register-password-confirm-input" type="password" required value={f.password_confirm} onChange={set("password_confirm")} /></div>
      <p className="text-[11px] text-slate-500">Almeno 10 caratteri con maiuscole, minuscole e numeri.</p>
      <label className="flex items-start gap-2 text-xs text-slate-600">
        <Checkbox data-testid="register-privacy-checkbox" checked={f.privacy_accepted} onCheckedChange={(v) => setF({ ...f, privacy_accepted: !!v })} />
        Ho letto l'informativa privacy (GDPR) e acconsento al trattamento dei dati per la gestione dell'account.
      </label>
      <Button data-testid="register-submit-btn" className="w-full rounded-full bg-sky-600 hover:bg-sky-700">Crea account</Button>
    </form>
  );
}

function OperatorRegister() {
  const [pre, setPre] = useState(null);
  const [usage, setUsage] = useState("");
  const [registered, setRegistered] = useState(false);
  const pro = usage && !["paziente", "struttura_sanitaria"].includes(usage);
  if (!pre) return <EmailConfirm onDone={(email, token) => setPre({ email, token })} />;
  if (registered && pro) return <OperatorDetails usage={usage} />;
  if (usage) return <RegisterEmailForm usage={usage} preEmail={pre.email} precheckToken={pre.token} onBack={() => setUsage("")} onRegistered={() => pro && setRegistered(true)} />;
  return <OperatorPath onSelect={setUsage} />;
}

function RegisterOptions() {
  const [email, setEmail] = useState(false);
  if (email) return <RegisterEmailForm onBack={() => setEmail(false)} />;
  return (
    <div className="space-y-2.5" data-testid="register-options">
      <ProviderButtons verb="register" />
      <button data-testid="register-email-btn" onClick={() => setEmail(true)} className="flex w-full items-center gap-3 rounded-xl border border-cyan-300 bg-cyan-50 px-4 py-3 text-sm font-bold text-cyan-900 transition-colors hover:bg-cyan-100">
        <Mail className="h-5 w-5" /> REGISTRATI CON EMAIL
      </button>
      <p className="pt-1 text-[11px] leading-snug text-slate-500">SPID e CIE sono in <b>ambiente sandbox</b> di test: non sono ancora operativi finché non sarà attiva la convenzione con un provider autorizzato.</p>
    </div>
  );
}

export default function LoginPage() {
  const [params] = useSearchParams();
  const operator = params.get("area") === "operatori";
  const [tab, setTab] = useState(operator || params.get("tab") === "registrati" ? "register" : "login");
  const [forgot, setForgot] = useState(false);
  return (
    <div className="grid min-h-screen bg-slate-50 lg:grid-cols-[1.1fr_1fr]" data-testid="auth-page">
      <div className="relative hidden overflow-hidden bg-gradient-to-br from-sky-100 via-white to-cyan-50 lg:block">
        <div className="absolute inset-0 grid-bg" />
        <div className="relative flex h-full flex-col justify-between p-10">
          <Brand />
          <div className="flex items-end gap-6">
            <img src={AVATAR_SRC} alt="Doctor Selvix" className="h-[62vh] w-auto object-contain drop-shadow-2xl" />
            <div className="mb-16 max-w-xs">
              <p className="glass rounded-2xl p-4 text-sm text-slate-700">Benvenuto! Scegli come accedere: un unico profilo, più modalità di accesso collegabili in sicurezza.</p>
              <p className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-500"><ShieldCheck className="h-3.5 w-3.5 text-cyan-700" /> Non condividere mai password o codici con Doctor Selvix.</p>
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-md rise">
          <div className="mb-6 lg:hidden"><Brand /></div>
          <div className="glass rounded-3xl p-6 sm:p-8">
            <div className="mb-6 flex rounded-full bg-slate-100 p-1">
              {[["login", "ACCEDI"], ["register", "REGISTRATI"]].map(([k, l]) => (
                <button key={k} data-testid={`auth-tab-${k}`} onClick={() => { setTab(k); setForgot(false); }} className={`flex-1 rounded-full py-2 text-xs font-extrabold tracking-widest transition-colors ${tab === k ? "bg-white text-sky-700 shadow" : "text-slate-500"}`}>{l}</button>
              ))}
            </div>
            {operator && <div className="mb-4" data-testid="operator-area-header"><p className="font-display text-lg font-extrabold text-rose-600">Area operatori</p><p className="text-xs text-slate-500">Registrazione prestatore di servizio</p></div>}
            {tab === "register" ? (operator ? <OperatorRegister /> : <RegisterOptions />) : forgot ? <ForgotForm onBack={() => setForgot(false)} /> : (
              <div className="space-y-5">
                <LoginForm onForgot={() => setForgot(true)} />
                <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest text-slate-400"><span className="h-px flex-1 bg-slate-200" />oppure<span className="h-px flex-1 bg-slate-200" /></div>
                <ProviderButtons verb="login" />
              </div>
            )}
          </div>
          <Link to="/" data-testid="back-to-clinic-link" className="mt-4 block text-center text-xs font-semibold text-slate-500 hover:text-sky-700">← Torna alla clinica virtuale</Link>
        </div>
      </div>
    </div>
  );
}
